import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface LevelUpInfo {
  newLevel: number;
  artistId: string;
  artistName: string;
  accentColor: string;
}

function getDepthStorageKey(userId: string, artistId: string) {
  return `depth_lv_${userId}_${artistId}`;
}

const LEVEL_THRESHOLDS = [0, 10, 30, 60];

function calcFanLevel(supportExp: number, checkInExp: number, hasVeteranBonus: boolean): number {
  const totalExp = supportExp + checkInExp + (hasVeteranBonus ? 1 : 0);
  let fanLevel = 0;
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (totalExp >= LEVEL_THRESHOLDS[i]) {
      fanLevel = i;
      break;
    }
  }
  return fanLevel;
}

export function useResonanceLevelUp() {
  const [levelUpInfo, setLevelUpInfo] = useState<LevelUpInfo | null>(null);

  const checkLevelUp = useCallback(async (
    userId: string,
    artistId: string,
    artistName: string,
    accentColor: string
  ) => {
    try {
      const { data, error } = await supabase.rpc('get_user_artist_depth_details', {
        p_user_id: userId,
        p_artist_id: artistId,
      });
      if (error || !data) return;

      // RPCの戻り値が配列の場合と単一オブジェクトの場合の両方に対応
      const record = Array.isArray(data) ? data[0] : data;
      if (!record) return;

      const newLevel: number =
        record.fanLevel != null
          ? record.fanLevel
          : calcFanLevel(
              record.supportExp ?? 0,
              record.checkInExp ?? 0,
              record.hasVeteranBonus ?? false
            );

      const storageKey = getDepthStorageKey(userId, artistId);
      const savedLevel = parseInt(localStorage.getItem(storageKey) || '0', 10);

      if (newLevel > savedLevel && newLevel > 0) {
        localStorage.setItem(storageKey, String(newLevel));
        setLevelUpInfo({ newLevel, artistId, artistName, accentColor });
      }
    } catch (err) {
      console.error('Level up check failed:', err);
    }
  }, []);

  const clearLevelUp = useCallback(() => {
    setLevelUpInfo(null);
  }, []);

  return { levelUpInfo, checkLevelUp, clearLevelUp };
}
